<template>
    <div class="chart-preview">
        <div class="row">
            <div v-if="!if_loading" :class="chart_preview_classname">
                <div class="mb-3">
                    <div class="card chart-container" v-if="local_chart_selected.type">
                        <div class="card-body">
                            <div class="row">
                                <div class="mb-2">
                                    <label class="form-label d-block">Preview Size: </label>
                                    <button 
                                        class="btn mb-2"
                                        :class="{ 'btn-primary': preview_size_selected === 'small', 'btn-secondary': preview_size_selected !== 'small' }"
                                        @click="changePreviewSize('small')"
                                    >
                                        Small
                                    </button>

                                    <button 
                                        class="btn mb-2"
                                        :class="{ 'btn-primary': preview_size_selected === 'medium', 'btn-secondary': preview_size_selected !== 'medium' }"
                                        @click="changePreviewSize('medium')"
                                    >
                                        Medium
                                    </button>

                                    <button 
                                        class="btn mb-2"
                                        :class="{ 'btn-primary': preview_size_selected === 'large', 'btn-secondary': preview_size_selected !== 'large' }"
                                        @click="changePreviewSize('large')"
                                    >
                                        Large
                                    </button>

                                    <button 
                                        class="btn mb-2"
                                        :class="{ 'btn-primary': preview_size_selected === 'xl', 'btn-secondary': preview_size_selected !== 'xl' }"
                                        @click="changePreviewSize('xl')"
                                    >
                                        XL
                                    </button>
                                </div>
                            </div>

                            <div v-if="local_chart_selected.type !== 'information_card'" class="row mb-3">
                                <div class="col-12 mb-3" v-if="local_data_blends && local_data_blends.length > 0">
                                    <label class="form-label">Client's Data Blend to Validate Against</label>
                                    <accordion-select 
                                        :options="formattedDataBlends"
                                        v-model="validate_data_blend_formatted"
                                        @update:modelValue="handleDataBlendChange"
                                        :splitText="false"
                                    />
                                </div>
                            </div>
                            <chart-template
                                :chart="local_chart_selected" 
                                :allow_deep_dive="false"
                                :if_benchmark_report="false" 
                                :if_on_report_page="false"
                            />
                        </div>
                    </div>

                    <div class="card" v-else>
                        <div class="card-body pt-5 pb-4">
                            <h1 class="text-primary text-center"><i class="fas fa-chart-simple"></i></h1>
                            <h2 class="text-primary text-center"><em>No chart type has been selected</em></h2>
                        </div>
                    </div>
                </div>
            </div>

            <div v-else>
                <div class="card">
                    <div class="card-body pt-5 pb-4">
                        <h1 class="text-primary text-center">
                            <div class="spinner-border spinner-border-sm float-left" role="status"></div>
                        </h1>
                        <h2 class="text-primary text-center"><em>Loading Chart Data</em></h2>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import AccordionSelect from '../../components/AccordionSelect.vue';

// This will be a wrapper vue for Chart on Chart Builder
export default defineComponent({
    name: 'ChartPreview',
    components: {
        AccordionSelect
    },
    props: {
        chart_selected: {
            type: Object,
            required: true
        },
        if_loading: {
            type: Boolean,
            required: true
        },
        chart_type: {
            type: String,
            default: 'line'
        },
        render_large_chart: {
            type: Boolean,
            required: true
        },
        data_blends: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            local_chart_selected: {},
            local_chart_data_blend_type: '',
            preview_size_selected: 'large',
            chart_preview_classname: this.render_large_chart ? 'col-9' : 'col',
            local_data_blends: [],
            validate_data_blend: null,
            validate_data_blend_formatted: null
        };
    },
    computed: {
        formattedDataBlends() {
            if (!this.local_data_blends || !Array.isArray(this.local_data_blends)) {
                return [];
            }
            return this.local_data_blends.map(blend => ({
                value: blend.id.toString(),
                text: blend.table_name || '',
                category: '',
                subcategory: ''
            }));
        }
    },
    watch: {
        chart_type: {
            handler(newVal) {
                this.local_chart_selected.type = this.chart_type;
                this.$forceUpdate();
            },
            deep: true // This makes sure the watcher is looking for changes deeply within the object
        },
        chart_selected: {
            handler(newVal) {
                // console.log('chart_preview')
                // if(this.chart_selected.data_blend_type !== this.local_chart_data_blend_type) {
                //     this.setDefaultDataBlend();
                // };
                this.local_chart_selected = this.chart_selected;
                this.filterDataBlendsByType();
                this.$forceUpdate();
            },
            deep: true // This makes sure the watcher is looking for changes deeply within the object
        },
    },
    beforeMount() {
        this.local_chart_selected = this.chart_selected;
        this.local_chart_data_blend_type = this.chart_selected.data_blend_type;
        this.filterDataBlendsByType();
    },
    methods: {
        filterDataBlendsByType() {
            if (this.local_chart_selected.data_blend_type) {
                this.local_data_blends = this.data_blends.filter(blend => blend.type === this.local_chart_selected.data_blend_type);
            }
            if(this.chart_selected.client_id) {
                this.local_data_blends = this.local_data_blends.filter(blend => blend.client_id === this.chart_selected.client_id);
            }
            this.setDefaultDataBlend();
        },
        getClassNameBySize(size) {
            if (this.render_large_chart) {
                if (size === 'small') return 'col-4';
                if (size === 'medium') return 'col-6';
                if (size === 'large') return 'col-9';
            } else {
                if (size === 'small') return 'col-6';
                if (size === 'medium') return 'col-9';
                if (size === 'large') return 'col-12';
            }

            return 'col';
        },
        changePreviewSize(size) {
            this.preview_size_selected = size;
            this.chart_preview_classname = this.getClassNameBySize(size);
            //emit the change
            this.$emit('changePreviewSize', size);
        },
        handleDataBlendChange(formattedValue) {
            // Convert the formatted value back to the actual data blend object
            if (formattedValue && formattedValue.value) {
                const matchingBlend = this.local_data_blends.find(
                    blend => blend.id.toString() === formattedValue.value.toString()
                );
                if (matchingBlend) {
                    this.validate_data_blend = matchingBlend;
                    // Emit the change event with the selected client's data blend
                    this.$emit('validateDataBlendChange', matchingBlend);
                }
            } else {
                this.validate_data_blend = null;
                this.$emit('validateDataBlendChange', null);
            }
        },
        setDefaultDataBlend() {
            // Find the matching data blend from the data_blends array by ID
            if (this.local_chart_selected.data_blend && this.local_chart_selected.data_blend.id && this.local_data_blends && this.local_data_blends.length > 0) {
                const matchingBlend = this.local_data_blends.find(
                    blend => blend.id === this.local_chart_selected.data_blend.id
                );
                if (matchingBlend) {
                    this.validate_data_blend = matchingBlend;
                    // Set the formatted value for accordion-select
                    this.validate_data_blend_formatted = {
                        value: matchingBlend.id.toString(),
                        text: matchingBlend.table_name || '',
                        category: '',
                        subcategory: null
                    };
                }
            } else {
                this.validate_data_blend_formatted = null;
            }
        }
    }
});
</script>
